import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import Filter from './Filter';
import { Button, message, Slider, Tabs } from 'antd';
import styled from '@emotion/styled';
import { useInViewport, useRequest } from 'ahooks';
import { getVoiceOverview } from './service';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import useModal from 'hooks/useModal';
import ReviewModal, { ReviewModalProps } from './ReviewModal';
import { initEcharts } from 'lib/echarts';
import TableContent from './TableContent';
import reviewStarImg from './assets/reviewStar.png';
import { StarRate } from 'views/Review/components/Profile';
import { formatNumber } from 'utils/numberFormat';

function UserVoice({
  visible,
  departments,
}: {
  visible: boolean;
  departments: Dashboard.Department[];
}) {
  const { t } = useTranslate();

  const reviewModalState = useModal<Omit<ReviewModalProps, 'onOpenConversation'>>();

  const reviewModal = reviewModalState.meta && (
    <ReviewModal
      key={reviewModalState.meta.reviewIds.join(',')}
      isVoice
      {...reviewModalState.meta}
      visible={reviewModalState.visible}
      onCancel={() => reviewModalState.hide()}
    />
  );

  const [filterValue, setFilterValue] = useState<ReviewAnalysis.OverviewFilter>(() => ({
    platform: undefined,
    storeInfo: {},
    startTime: dayjs().subtract(90, 'days').startOf('days').valueOf().toString(),
    endTime: dayjs().subtract(1, 'days').endOf('days').valueOf().toString(),
    stars: [],
    spuKeys: [],
    itemRange: 'ALL',
    sentimentType: 2,
  }));

  const { loading, data } = useRequest(() => getVoiceOverview(filterValue), {
    ready: visible,
    refreshDeps: [filterValue],
    onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
  });

  const { categoryMap, totalProblemCount } = useMemo(() => {
    const categoryMap = data?.problemCategoryList?.reduce<number[]>((res, { id, problemCount }) => {
      res[id] = problemCount;
      return res;
    }, []);

    const totalProblemCount =
      data?.problemCategoryList?.reduce((res, { problemCount }) => res + problemCount, 0) || 0;

    return {
      categoryMap,
      totalProblemCount,
    };
  }, [data]);

  const chartDomRef = useRef(null);

  const [inViewport] = useInViewport(chartDomRef);

  const isAdvantage = filterValue.sentimentType === 1;

  useEffect(() => {
    if (!inViewport || !chartDomRef.current || !categoryMap) {
      return;
    }

    const chart = initEcharts(chartDomRef.current);

    const max = totalProblemCount || 1;
    const max2 = max + max * 0.15;

    chart.setOption({
      radar: {
        indicator: [
          { text: t('knowledge.商品相关'), max },
          { text: t('物流配送'), max },
          { text: t('活动营销'), max },
          { text: t('服务体验'), max },
        ],
        radius: '80%',
        splitNumber: 1,
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: isAdvantage ? '#E2EBFE' : '#F4D4B1',
          },
        },
        splitArea: {
          areaStyle: {
            color: isAdvantage ? '#F5FBFF' : '#FFEDDF',
          },
        },
        axisLine: {
          lineStyle: {
            type: 'dashed',
            color: isAdvantage ? '#E7F1FF' : '#F4D4B1',
          },
        },
        axisName: {
          show: false,
        },
      },
      series: [
        {
          type: 'radar',
          data: [
            {
              name: 'max',
              value: [max2, max2, max2, max2],
            },
          ],
          itemStyle: {
            color: isAdvantage ? '#709CFF' : '#FE974F',
          },
          animation: false,
        },
        {
          type: 'radar',
          data: [
            {
              name: 'value',
              value: [categoryMap[1], categoryMap[2], categoryMap[4], categoryMap[3]],
            },
          ],
          areaStyle: {
            opacity: 1,
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: isAdvantage ? '#62A8FF' : '#FACEAE',
                },
                {
                  offset: 1,
                  color: isAdvantage ? 'var(--fett-scenario-accent-text)' : '#FF8733',
                },
              ],
            },
          },
          symbol: 'none',
          itemStyle: {
            show: false,
            color: 'transparent',
          },
        },
      ],
    });

    const handler = () => {
      chart.resize();
    };

    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);

      chart.dispose();
    };
  }, [inViewport, categoryMap, totalProblemCount, isAdvantage, t]);

  const filter = (
    <Filter isVoice departments={departments} value={filterValue} onChange={setFilterValue} />
  );

  const style = visible ? undefined : { display: 'none' };

  if (!data || !categoryMap) {
    return (
      <div style={style}>
        {filter}
        {loading && <LoadingOutlined />}
      </div>
    );
  }

  const {
    starReviewCount1,
    starReviewCount2,
    starReviewCount3,
    starReviewCount4,
    starReviewCount5,
    unCategorizedCount,
    unCategorizedReviewIds,
  } = data;

  const reviewCount =
    starReviewCount1 + starReviewCount2 + starReviewCount3 + starReviewCount4 + starReviewCount5;

  const formatter = (num: number) => formatNumber(num, '0,0');
  const percenter = (num: number) => formatNumber(num, '0.00%');

  const stars = (
    <section>
      <div>
        <img src={reviewStarImg} alt="reviewStar" />
        <div>{formatter(reviewCount)}</div>
        <span>{t('总评价数')}</span>
      </div>
      <ul>
        <li>
          <div>
            <div>
              <StarRate disabled value={5} />
            </div>
            <div>
              <StarRate disabled value={3} />
            </div>
            <div>
              <StarRate disabled value={1} />
            </div>
          </div>
          <section>
            <div>
              <Slider value={(starReviewCount5 / reviewCount) * 100} />
            </div>
            <div>
              <Slider value={(starReviewCount3 / reviewCount) * 100} />
            </div>
            <div>
              <Slider value={(starReviewCount1 / reviewCount) * 100} />
            </div>
          </section>
          <div>
            <div>
              {formatter(starReviewCount5)}（{t('trade.数量')}）
            </div>
            <div>
              {formatter(starReviewCount3)}（{t('trade.数量')}）
            </div>
            <div>
              {formatter(starReviewCount1)}（{t('trade.数量')}）
            </div>
          </div>
          <span>
            <div>{percenter(starReviewCount5 / reviewCount)}</div>
            <div>{percenter(starReviewCount3 / reviewCount)}</div>
            <div>{percenter(starReviewCount1 / reviewCount)}</div>
          </span>
        </li>
        <li>
          <div>
            <div>
              <StarRate disabled value={4} />
            </div>
            <div>
              <StarRate disabled value={2} />
            </div>
          </div>
          <section>
            <div>
              <Slider value={(starReviewCount4 / reviewCount) * 100} />
            </div>
            <div>
              <Slider value={(starReviewCount2 / reviewCount) * 100} />
            </div>
          </section>
          <div>
            <div>
              {formatter(starReviewCount4)}（{t('trade.数量')}）
            </div>
            <div>
              {formatter(starReviewCount2)}（{t('trade.数量')}）
            </div>
          </div>
          <span>
            <div>{percenter(starReviewCount4 / reviewCount)}</div>
            <div>{percenter(starReviewCount2 / reviewCount)}</div>
          </span>
        </li>
      </ul>
    </section>
  );

  const category = (
    <div>
      <div>
        <span>{t('物流配送')}</span>
        <section>
          <span>{t('knowledge.商品相关')}</span>
          <div ref={chartDomRef} />
          <span>{t('活动营销')}</span>
        </section>
        <span>{t('服务体验')}</span>
      </div>
      <section>
        <header>
          <h4>{isAdvantage ? t('优点总数') : t('问题总数')}</h4>
          <div>{totalProblemCount}</div>
        </header>
        <ul>
          {[t('knowledge.商品相关'), t('物流配送'), t('服务体验'), t('活动营销')].map(
            (label, index) => {
              return (
                <li>
                  <div>{label}</div>
                  <dl>
                    <dt>{isAdvantage ? t('优点数量') : t('问题数量')}</dt>
                    <dd>{formatter(categoryMap[index + 1])}</dd>
                  </dl>
                  <dl>
                    <dt>{t('占比')}</dt>
                    <dd>{percenter(categoryMap[index + 1] / totalProblemCount)}</dd>
                  </dl>
                </li>
              );
            },
          )}
        </ul>
      </section>
    </div>
  );

  return (
    <Container style={style} isAdvantage={String(isAdvantage)}>
      {filter}
      {stars}
      <div>
        <header>
          <Tabs
            activeKey={filterValue.sentimentType ? String(filterValue.sentimentType) : '2'}
            onChange={(value) =>
              setFilterValue({
                ...filterValue,
                sentimentType: Number(value) as any,
              })
            }
            tabBarExtraContent={
              <Button
                type="text"
                onClick={() =>
                  reviewModalState.show({
                    reviewIds: unCategorizedReviewIds,
                    count: unCategorizedCount,
                    uncategorized: true,
                  })
                }
              >
                {t('查看未分类评价')}({unCategorizedCount})
              </Button>
            }
          >
            <Tabs.TabPane key="2" tab={t('问题发现')} />
            <Tabs.TabPane key="1" tab={t('优点总结')} />
          </Tabs>
        </header>
        {category}
        <section>
          <TableContent
            isVoice
            isAdvantage={isAdvantage}
            filter={filterValue}
            categoryMap={categoryMap}
          />
        </section>
      </div>
      {reviewModal}
    </Container>
  );
}

const Container = styled.div<{ isAdvantage?: string }>`
  > section {
    display: flex;
    align-items: stretch;
    height: 140px;
    background: var(--fett-bg-panel);
    > div {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      align-items: center;
      width: 140px;
      padding: 8px 0;
      background: linear-gradient(180deg, #eef7ff 0%, #d9ecff 100%);
      [data-color-mode='dark'] & {
        background: linear-gradient(180deg, var(--fett-bg-elevated) 0%, var(--fett-bg-panel) 100%);
      }
      > img {
        width: 56px;
      }
      > div {
        font-size: 22px;
        font-weight: 600;
        color: var(--fett-scenario-accent-text);
      }
    }
    > ul {
      flex: 1 0 0;
      display: flex;
      margin: 0;
      padding: 0;
      list-style: none;
      > li {
        flex: 1 0 0;
        display: flex;
        padding: 16px 12px;
        > * {
          flex: 0 0 auto;
          > div {
            display: flex;
            align-items: center;
            height: 36px;
            > * {
              flex: 1 0 0;
            }
            > .ant-slider {
              pointer-events: none;
              .ant-slider-track {
                background-color: #336fff;
              }
              .ant-slider-handle {
                border-color: #336fff;
              }
            }
          }
        }
        > section {
          flex: 1 0 0;
        }
        > section + div {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
      }
    }
  }
  > div {
    > header {
      background: var(--fett-bg-panel);
    }
    > section {
      padding: 16px 24px;
      background: var(--fett-bg-panel);
      > header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        .ant-switch {
          margin-left: 4px;
        }
      }
    }
    > div {
      display: flex;
      align-items: center;
      height: 312px;
      padding: 16px;
      background: var(--fett-bg-panel);
      > div {
        flex: 0 0 auto;
        width: 350px;
        height: 100%;
        display: flex;
        align-items: stretch;
        > span {
          flex: 0 0 auto;
          align-self: center;
          width: 78px;
          &:first-of-type {
            text-align: right;
          }
        }
        > section {
          flex: 1 0 0;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          > span {
            flex: 0 0 auto;
            height: 42px;
            text-align: center;
            &:first-of-type {
              display: flex;
              flex-direction: column;
              justify-content: flex-end;
            }
          }
          > div {
            flex: 1 0 0;
          }
        }
      }
      > section {
        flex: 1 0 0;
        margin-left: 16px;
        border: solid 1px #e9ebed;
        border-radius: 4px;
        [data-color-mode='dark'] & {
          border-color: var(--fett-border-default);
        }
        > header {
          display: flex;
          align-items: center;
          height: 50px;
          padding: 0 16px;
          background: ${({ isAdvantage }) => (isAdvantage === 'true' ? '#F4FAFF' : '#fff8f2')};
          [data-color-mode='dark'] & {
            background: ${({ isAdvantage }) =>
              isAdvantage === 'true'
                ? 'var(--fett-scenario-header-bg)'
                : 'rgba(254, 159, 79, 0.14)'};
          }
          > h4 {
            margin-bottom: 0;
            margin-right: 8px;
            font-size: 16px;
          }
          > .anticon {
            cursor: help;
          }
          > div {
            margin-left: 8px;
            font-size: 16px;
            color: ${({ isAdvantage }) =>
              isAdvantage === 'true' ? 'var(--fett-scenario-accent-text)' : '#fe974f'};
          }
        }
        > ul {
          margin: 0;
          padding: 16px;
          list-style: none;
          display: grid;
          gap: 10px;
          grid-template-columns: repeat(2, 1fr);
          @media (min-width: 2100px) {
            grid-template-columns: repeat(4, 1fr);
          }
          > li {
            display: flex;
            justify-content: space-around;
            align-items: center;
            position: relative;
            padding-top: 30px;
            background: #f9fbfa;
            [data-color-mode='dark'] & {
              background: var(--fett-bg-panel);
            }
            > div {
              position: absolute;
              top: 0;
              left: 0;
              display: inline-block;
              height: 20px;
              line-height: 20px;
              padding: 0 12px;
              background: ${({ isAdvantage }) => (isAdvantage === 'true' ? '#CCE6FF' : '#ffeddf')};
              border-radius: 0 0 8px 0;
              [data-color-mode='dark'] & {
                background: ${({ isAdvantage }) =>
                  isAdvantage === 'true'
                    ? 'var(--fett-scenario-accent-strong-bg)'
                    : 'rgba(254, 159, 79, 0.26)'};
              }
            }
            > dl {
              display: flex;
              flex-direction: column;
              align-items: center;
              > dt {
                font-weight: 400;
                color: var(--fett-text-tertiary);
              }
              > dd {
                margin-bottom: 0;
                font-size: 18px;
                font-weight: 500;
                color: var(--fett-text-primary);
              }
            }
          }
        }
      }
    }
  }
`;

export default UserVoice;
